import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MessageSquare, Send, User, RefreshCw, ChevronLeft,
  Hash, Plus, Users, X, Pencil, Check, UserPlus, Trash2,
  Lock, Unlock, Search, Inbox, Bell, Bookmark, Archive, Printer, ChevronDown,
  Briefcase, Link2, Link2Off, Clock,
} from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import toast from '../lib/toast';
import {
  listFirmConversations,
  listFirmPortalMessages,
  sendPortalMessage,
  markFirmMessagesRead,
  listFirmChannels,
  createFirmChannel,
  listFirmMessages,
  sendFirmMessage,
  // updateFirmMessage replaced by editFirmMessageRpc (preserves edit history)
  listFirmPrivateChannels,
  createPrivateChannel,
  addChannelMember,
  markChannelRead,
  getChannelUnreadCounts,
  listFirmUsers,
  listContacts,
  listThreadParticipants,
  addThreadParticipant,
  uploadMessageAttachment,
  softDeleteFirmMessage,
  softDeletePortalMessage,
  setPortalThreadPrivacy,
  getMessagingOverview,
  firmMessageThreadCounts,
  togglePinFirmMessage,
  archiveFirmChannel,
  editFirmMessageRpc,
  listPinnedMessages,
  setChannelMatter,
  fetchMatterSummary,
  listMattersForPicker,
  fireWebPush,
  getFirmDlpSettings,
  setChannelEncryption,
} from '../lib/db';
import MessageAttachments, { PendingAttachments, AttachButton } from '../components/messages/MessageAttachments';
import MessageReactions, { useMessageReactions } from '../components/messages/MessageReactions';
import { useMentionPicker } from '../components/messages/MentionPicker';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import RichText, { FORMATTING_HINT } from '../components/messages/RichText';
import MessageSearch from '../components/messages/MessageSearch';
import ThreadPanel from '../components/messages/ThreadPanel';
import useTypingIndicator from '../components/messages/useTypingIndicator';
import EditHistoryModal from '../components/messages/EditHistoryModal';
import ScheduleSendMenu from '../components/messages/ScheduleSendMenu';
import BookmarksDrawer, { useBookmarks } from '../components/messages/Bookmarks';
import { scanText as scanDlp, DLP_RULE_LABELS } from '../components/messages/dlpScanner';
import { isEncryptedEnvelope } from '../components/messages/cryptoE2EE';
import { useEncryption } from '../contexts/EncryptionContext';
import { useRoute } from '../lib/router';

// ─── Shared helpers ──────────────────────────────────────────────────────────
function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatGroupDate(ts) {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function contactName(c) {
  if (!c) return 'Unknown';
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.organization || c.email || 'Unknown';
}

function initials(c) {
  if (!c) return '?';
  const n = contactName(c);
  return n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

// ─── Matter picker for channel linking ──────────────────────────────────
function MatterPickerModal({ firmId, currentMatterId, onPick, onClose }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    if (!firmId) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listMattersForPicker(firmId, query).then(({ data }) => {
        if (cancelled) return;
        setResults(Array.isArray(data) ? data : []);
        setLoading(false);
      });
    }, 150);
    return () => { cancelled = true; clearTimeout(t); };
  }, [firmId, query]);

  const choose = async (matterId) => {
    setBusy(true);
    await onPick(matterId);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase size={14} className="text-slate-500" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Link channel to a matter</span>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by matter title or number…"
              className="w-full pl-7 pr-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {currentMatterId && (
            <button
              onClick={() => choose(null)}
              disabled={busy}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition disabled:opacity-50"
            >
              <Link2Off size={13} /> Unlink current matter
            </button>
          )}
          <div className="max-h-64 overflow-y-auto -mx-1">
            {loading ? (
              <p className="px-3 py-4 text-sm text-slate-500 text-center">Loading…</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-500 text-center">
                {query ? 'No matters match.' : 'No matters in this firm yet.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {results.map((m) => {
                  const isCurrent = m.id === currentMatterId;
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => choose(m.id)}
                        disabled={busy || isCurrent}
                        className={`w-full flex items-start gap-3 px-3 py-2 rounded-lg text-left transition ${
                          isCurrent
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        } disabled:opacity-60`}
                      >
                        <Briefcase size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{m.title}</p>
                          <p className="text-xs text-slate-500">{m.matter_number || '—'} · {m.status || 'open'}</p>
                        </div>
                        {isCurrent && <Check size={13} className="text-indigo-600 flex-shrink-0 mt-1" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Team Chat ───────────────────────────────────────────────────────────────

function NewChannelModal({ firmId, userId, onCreated, onClose }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    const { data, error: err } = await createFirmChannel(firmId, name.trim(), desc.trim(), userId);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onCreated(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">Create a channel</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Channel name</label>
            <div className="relative">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. billing, team-updates"
                className="w-full pl-8 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Description <span className="text-slate-400">(optional)</span></label>
            <input
              value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="What's this channel for?"
              className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
            <button type="submit" disabled={saving || !name.trim()} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition">
              {saving ? 'Creating…' : 'Create channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Add member to existing group / DM ──────────────────────────────────────
function AddMemberModal({ firmId, channel, currentMembers, onAdded, onClose }) {
  const [allUsers,  setAllUsers]  = useState([]);
  const [selected,  setSelected]  = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    listFirmUsers(firmId).then(({ data }) => {
      const inChannel = new Set((currentMembers || []).map((m) => m.id));
      setAllUsers((data || []).filter((u) => !inChannel.has(u.id)));
    });
  }, [firmId, currentMembers]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const add = async () => {
    if (!selected.length) return;
    setAdding(true);
    const errs = [];
    const added = [];
    for (const uid of selected) {
      const { error: err } = await addChannelMember(channel.id, uid);
      if (err) errs.push(err.message);
      else {
        const u = allUsers.find((x) => x.id === uid);
        if (u) added.push(u);
      }
    }
    setAdding(false);
    if (errs.length) { setError(errs.join('; ')); return; }
    onAdded(added);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">Add people</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">
          New members will be able to see all previous messages in this conversation.
        </p>

        <div className="space-y-1 max-h-60 overflow-y-auto">
          {allUsers.length === 0 && (
            <p className="text-xs text-slate-400 py-2 text-center">All team members are already in this chat.</p>
          )}
          {allUsers.map((u) => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
            const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
            const sel  = selected.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition
                  ${sel ? 'bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0
                  ${sel ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  {ini}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.role || u.email}</p>
                </div>
                {sel && <Check size={15} className="text-indigo-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button
            onClick={add}
            disabled={!selected.length || adding || allUsers.length === 0}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
          >
            {adding ? 'Adding…' : `Add ${selected.length > 0 ? selected.length : ''} ${selected.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamChatThread({ channel, firmId, userId, userProfile, channelLabel, onMembersChanged, onChannelRead, bookmarks }) {
  const [messages,      setMessages]      = useState([]);
  // Restore any previously saved draft for this channel + user pair.
  const draftKey = `draft:firm:${channel.id}:${userId}`;
  const [body, setBody] = useState(() => {
    try { return localStorage.getItem(draftKey) || ''; } catch { return ''; }
  });
  const [loading,       setLoading]       = useState(true);
  const [sending,       setSending]       = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [editBody,      setEditBody]      = useState('');
  const [savingEdit,    setSavingEdit]    = useState(false);
  const [showMembers,   setShowMembers]   = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [localMembers,  setLocalMembers]  = useState(channel.members || []);
  // Files staged in the composer but not yet sent.
  const [pendingFiles,  setPendingFiles]  = useState([]);
  const [deletingId,    setDeletingId]    = useState(null);
  // Threaded replies
  const [threadCounts,  setThreadCounts]  = useState({}); // { [parentId]: { reply_count, last_reply_at } }
  const [activeThread,  setActiveThread]  = useState(null); // the parent message
  // Edit history popup
  const [historyOf,     setHistoryOf]     = useState(null); // { id, body }
  // Pinned messages
  const [pinned,        setPinned]        = useState([]);
  const [pinnedOpen,    setPinnedOpen]    = useState(true);
  // Matter linking
  const [linkedMatter,  setLinkedMatter]  = useState(null);     // { id, matter_number, title }
  const [showMatterPicker, setShowMatterPicker] = useState(false);
  // Scheduled send
  const [scheduledFor,  setScheduledFor]  = useState(null);     // Date | null
  // DLP — firm settings + live scan results for the current draft.
  const [dlpSettings,   setDlpSettings]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const _dlpSettingsLoaded = !!dlpSettings;
  // End-to-end encryption for channels marked `is_encrypted`. Pulled from
  // the global EncryptionProvider so unlock state is shared across the app.
  const enc = useEncryption();
  // Decrypted plaintext per message id — built lazily as messages render.
  // The map is reset when the channel changes (key on channel.id below).
  const [decryptedById, setDecryptedById] = useState({});
  const bottomRef = useRef(null);
  const editRef   = useRef(null);
  const composerRef = useRef(null);
  const mention = useMentionPicker(composerRef, { value: body, setValue: setBody });
  // Ephemeral typing indicator (Supabase Realtime broadcast).
  const typingMe = React.useMemo(() => ({
    id:   userId,
    name: [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim() || userProfile?.email || 'Someone',
  }), [userId, userProfile?.first_name, userProfile?.last_name, userProfile?.email]);
  const { typers, notifyTyping } = useTypingIndicator('firm', channel.id, typingMe);

  // Persist the composer body as a draft so users don't lose work on refresh
  // or accidental tab close. Cleared on successful send below.
  useEffect(() => {
    try {
      if (body) localStorage.setItem(draftKey, body);
      else      localStorage.removeItem(draftKey);
    } catch { /* quota / private mode — silently ignore */ }
  }, [body, draftKey]);

  // Batched fetch of reactions for the currently rendered messages.
  const messageIds = messages.map((m) => m.id).filter((id) => !String(id).startsWith('temp-'));
  const { byMsg: reactionsByMsg, refresh: refreshReactions } = useMessageReactions('firm', messageIds);

  // Keep localMembers in sync when the channel prop changes (e.g. after re-load)
  useEffect(() => {
    setLocalMembers(channel.members || []);
    // We deliberately depend only on channel.id, not channel.members.
    // localMembers is the working copy — once initialised it owns its
    // own state (so optimistic add/remove works). Re-running on every
    // members array reference change would clobber pending mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.id]);

  useEffect(() => {
    let isMounted = true;

    // ── Initial load ──────────────────────────────────────────────────────────
    setLoading(true);
    setMessages([]);
    setEditingId(null);

    listFirmMessages(channel.id).then(({ data }) => {
      if (!isMounted) return;
      setMessages(data || []);
      setLoading(false);
      markChannelRead(channel.id, userId);
      if (onChannelRead) onChannelRead(channel.id);
    });

    // Pull thread counts once per channel load. Realtime INSERTs on
    // firm_messages with a parent_id refresh the map (see below).
    firmMessageThreadCounts(channel.id).then(({ data }) => {
      if (!isMounted || !Array.isArray(data)) return;
      const map = {};
      for (const t of data) {
        map[t.parent_message_id] = { reply_count: t.reply_count, last_reply_at: t.last_reply_at };
      }
      setThreadCounts(map);
    });

    // Pinned messages strip data.
    listPinnedMessages(channel.id).then(({ data }) => {
      if (!isMounted) return;
      setPinned(Array.isArray(data) ? data : []);
    });

    // Linked matter — clear first so the previous channel's matter doesn't
    // flash, then resolve if the new channel has a matter_id.
    setLinkedMatter(null);
    if (channel.matter_id) {
      fetchMatterSummary(channel.matter_id).then(({ data }) => {
        if (isMounted && data) setLinkedMatter(data);
      });
    }

    // DLP policy for the composer warning strip. If no row exists, the
    // scanner returns an empty array — server-side defaults still apply
    // on INSERT, so the user just doesn't get a pre-send heads-up.
    getFirmDlpSettings(firmId).then(({ data }) => {
      if (isMounted) setDlpSettings(data || null);
    });

    // Reset the decrypted-cache when switching channels.
    setDecryptedById({});

    // ── Realtime subscription ────────────────────────────────────────────────
    const sub = supabase
      .channel(`team_msgs_${channel.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'firm_messages',
        filter: `channel_id=eq.${channel.id}`,
      }, async (payload) => {
        const msg = payload.new;
        // Thread replies don't belong in the main timeline; they refresh
        // the per-parent count instead.
        if (msg.parent_message_id) {
          if (!isMounted) return;
          setThreadCounts((prev) => ({
            ...prev,
            [msg.parent_message_id]: {
              reply_count:   (prev[msg.parent_message_id]?.reply_count || 0) + 1,
              last_reply_at: msg.created_at,
            },
          }));
          return;
        }
        if (msg.sender_id === userId) return; // own msg already added optimistically
        // Fetch sender name for the incoming message
        const { data: sender } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .eq('id', msg.sender_id)
          .maybeSingle();
        if (!isMounted) return;
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev; // dedup
          return [...prev, { ...msg, sender: sender || null }];
        });
        markChannelRead(channel.id, userId);
        if (onChannelRead) onChannelRead(channel.id);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'firm_messages',
        filter: `channel_id=eq.${channel.id}`,
      }, (payload) => {
        const msg = payload.new;
        if (!isMounted) return;
        setMessages((prev) =>
          prev.map((m) => m.id === msg.id
            ? {
                ...m,
                body:        msg.body,
                edited_at:   msg.edited_at,
                deleted_at:  msg.deleted_at,
                attachments: msg.attachments,
                mentions:    msg.mentions,
              }
            : m
          )
        );
      })
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(sub);
    };
  }, [channel.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Decrypt any encrypted messages whose plaintext we don't yet have. Runs
  // when messages change OR when the encryption state toggles to unlocked.
  // We don't tear plaintexts down on lock — that would just briefly flash
  // ciphertext on lock; instead the next channel switch resets the cache.
  useEffect(() => {
    if (!channel.is_encrypted || !enc.unlocked) return;
    let cancelled = false;
    (async () => {
      for (const m of messages) {
        if (decryptedById[m.id] !== undefined) continue;
        if (!isEncryptedEnvelope(m.body)) continue;
        const plain = await enc.decryptEnvelope(m.body);
        if (cancelled) return;
        setDecryptedById((prev) =>
          prev[m.id] !== undefined ? prev : { ...prev, [m.id]: plain ?? null }
        );
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, enc.unlocked, channel.is_encrypted]);

  // Focus edit input when edit mode opens
  useEffect(() => {
    if (editingId) editRef.current?.focus();
  }, [editingId]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    const files = pendingFiles;
    if (!text && files.length === 0) return;

    // Encrypted channels require an unlocked session. We DON'T encrypt
    // attachments here in v1 — only the body text is encrypted, mirroring
    // most enterprise chat encryption defaults. Attachments stay in
    // RLS-protected storage.
    if (channel.is_encrypted && !enc.unlocked) {
      toast.error('This channel is encrypted. Unlock encryption in Settings → Security to send.');
      return;
    }

    // Pre-flight DLP scan — match the server-side enforcement so the user
    // gets a clear error here instead of a generic insert failure. The
    // trigger will still reject if anything slips past. Scan runs on the
    // plaintext so the rule catches sensitive content even in encrypted
    // channels — the policy applies regardless of channel privacy.
    const dlpHits = scanDlp(text, dlpSettings);
    const blockingHit = dlpHits.find((h) => h.severity === 'block');
    if (blockingHit) {
      toast.error(
        `Blocked by firm policy: ${DLP_RULE_LABELS[blockingHit.rule] || blockingHit.rule} detected (${blockingHit.match}). Remove it and try again.`,
      );
      return;
    }

    setSending(true);

    // Pre-generate the message UUID so attachment storage paths can include
    // the final id — keeps the storage RLS clean and avoids orphan rows.
    const msgId = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const mentions = mention.consumeMentions();

    const scheduledIso = scheduledFor ? new Date(scheduledFor).toISOString() : null;

    // Scheduled messages don't get an optimistic row in the timeline —
    // they don't belong there until release_scheduled_messages() promotes
    // them. Live messages get the usual optimistic insertion.
    const temp = {
      id: msgId,
      body: text || null,
      sender_id: userId,
      sender: { id: userId, first_name: userProfile?.first_name, last_name: userProfile?.last_name, email: userProfile?.email },
      created_at: new Date().toISOString(),
      attachments: [],
      mentions,
      _optimistic: true,
    };
    if (!scheduledIso) setMessages((prev) => [...prev, temp]);
    setBody('');
    setPendingFiles([]);
    const wasScheduled = !!scheduledIso;
    setScheduledFor(null);

    // Upload attachments (if any) before inserting the message row so
    // `attachments` on the row is populated atomically.
    const attachments = [];
    for (const file of files) {
      const { data: a, error: upErr } = await uploadMessageAttachment(
        'firm',
        { firmId, channelId: channel.id, messageId: msgId },
        file,
      );
      if (upErr) {
        toast.error(`Upload failed (${file.name}): ${upErr.message}`);
        setSending(false);
        setMessages((prev) => prev.filter((m) => m.id !== msgId));
        // Put text + files back so the user can retry.
        setBody(text);
        setPendingFiles(files);
        return;
      }
      attachments.push(a);
    }

    // Encrypt the body for encrypted channels. We send the ciphertext as
    // the body field; the server stores it as text. The realtime UPDATE
    // round-trip carries the ciphertext too, which our render path
    // decrypts back via the decryptedById cache. We also pre-populate the
    // cache with the plaintext for the just-sent message so the user
    // immediately sees their own message without an extra decrypt pass.
    let bodyToSend = text || null;
    if (channel.is_encrypted && bodyToSend) {
      try {
        bodyToSend = await enc.encryptForChannel(bodyToSend);
        setDecryptedById((prev) => ({ ...prev, [msgId]: text }));
      } catch (err) {
        toast.error('Could not encrypt: ' + (err?.message || 'unknown error'));
        setSending(false);
        return;
      }
    }

    const { data, error } = await sendFirmMessage(firmId, channel.id, userId, {
      id:          msgId,
      body:        bodyToSend,
      attachments,
      mentions,
      scheduled_for: scheduledIso,
    });
    setSending(false);
    if (error) {
      toast.error('Failed to send: ' + error.message);
      if (!wasScheduled) setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setBody(text);
      setPendingFiles(files);
      if (wasScheduled) setScheduledFor(scheduledFor);
    } else if (wasScheduled) {
      toast.success(`Scheduled for ${new Date(scheduledIso).toLocaleString()}.`);
    } else {
      setMessages((prev) => prev.map((m) => m.id === msgId ? data : m));

      // Fire-and-forget push notifications to mentioned teammates. Filter
      // out self-mentions so we don't beep our own devices. The edge
      // function silently filters out user_ids outside the caller's firm,
      // so cross-firm leakage is impossible even if a buggy mention slips
      // through.
      const recipients = (mentions || []).filter((uid) => uid && uid !== userId);
      if (recipients.length > 0) {
        const senderName = [userProfile?.first_name, userProfile?.last_name]
          .filter(Boolean).join(' ').trim() || userProfile?.email || 'A teammate';
        const previewBody = (text || '').replace(/@\[([^\]]+)\]\([0-9a-f-]{36}\)/gi, '@$1').slice(0, 180);
        fireWebPush({
          user_ids: recipients,
          title:    `${senderName} mentioned you in #${channel.name}`,
          body:     previewBody || '(no text)',
          url:      `/messages?channel=${channel.id}`,
          tag:      `mention-${channel.id}`,
        });
      }
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditBody(msg.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const saveEdit = async (msgId) => {
    const text = editBody.trim();
    if (!text) return;
    setSavingEdit(true);
    // edit_firm_message RPC snapshots the prior body into firm_message_edits
    // so we keep an audit trail of every edit. The RPC returns the new
    // edited_at timestamp.
    const { data: newEditedAt, error } = await editFirmMessageRpc(msgId, text);
    setSavingEdit(false);
    if (error) {
      toast.error('Failed to save edit: ' + error.message);
      return;
    }
    setMessages((prev) => prev.map((m) =>
      m.id === msgId ? { ...m, body: text, edited_at: newEditedAt || new Date().toISOString() } : m
    ));
    setEditingId(null);
    setEditBody('');
  };

  const handleDelete = async (msg) => {
    if (!window.confirm('Delete this message? It will be replaced with a deleted-message marker for everyone in the channel.')) return;
    setDeletingId(msg.id);
    const { error } = await softDeleteFirmMessage(msg.id);
    setDeletingId(null);
    if (error) {
      toast.error('Could not delete: ' + error.message);
      return;
    }
    // Realtime UPDATE will reflect deleted_at; mirror locally so the UI
    // doesn't lag.
    setMessages((prev) => prev.map((m) =>
      m.id === msg.id ? { ...m, deleted_at: new Date().toISOString(), body: null, attachments: [] } : m
    ));
  };

  const handleArchiveChannel = async () => {
    if (!window.confirm(`Archive #${channel.name}? Members keep their history but the channel disappears from sidebars and no new messages can be sent.`)) return;
    const { error } = await archiveFirmChannel(channel.id, true);
    if (error) { toast.error('Could not archive: ' + error.message); return; }
    toast.success(`Archived #${channel.name}.`);
    if (onMembersChanged) onMembersChanged(); // reuse the reload callback
  };

  const handleExportThread = () => {
    // Print-friendly view of the whole channel using the browser's print
    // pipeline. The user can save as PDF from there. No extra dependency.
    const w = window.open('', '_blank', 'noopener');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups to export.'); return; }
    const title = `#${channel.name} — ${new Date().toLocaleString()}`;
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const rows = messages
      .filter((m) => !m.parent_message_id)
      .map((m) => {
        const sender = m.sender ? [m.sender.first_name, m.sender.last_name].filter(Boolean).join(' ').trim() || m.sender.email : 'Unknown';
        const when = new Date(m.created_at).toLocaleString();
        const body = m.deleted_at ? '<em>Message deleted</em>' : esc(m.body || '').replace(/\n/g, '<br>');
        const attach = (m.attachments || []).map((a) => `<div style="font-size:12px;color:#666;margin-top:4px">📎 ${esc(a.name)} (${(a.size || 0) > 1024 ? Math.round((a.size || 0) / 1024) + ' KB' : (a.size || 0) + ' B'})</div>`).join('');
        return `<div style="margin:14px 0;padding:10px 12px;border-left:3px solid #6366f1;background:#f8fafc">
          <div style="font-size:12px;color:#475569"><strong>${esc(sender)}</strong> · ${esc(when)}${m.edited_at ? ' · <em>edited</em>' : ''}</div>
          <div style="margin-top:4px;font-size:14px;color:#1e293b">${body}</div>
          ${attach}
        </div>`;
      }).join('');
    w.document.write(`<!doctype html><html><head><title>${esc(title)}</title>
      <meta charset="utf-8">
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 24px auto; padding: 0 16px; color: #0f172a; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { color: #64748b; font-size: 12px; margin-bottom: 16px; }
        @media print { body { margin: 0; } }
      </style>
    </head><body>
      <h1>${esc(title)}</h1>
      <div class="meta">Exported ${esc(new Date().toLocaleString())} · ${messages.length} messages</div>
      ${rows}
      <${'sc'}ript>setTimeout(() => window.print(), 200);</${'sc'}ript>
    </body></html>`);
    w.document.close();
  };

  const isFirmLeader = userProfile?.role === 'Admin' || userProfile?.role === 'Owner' || userProfile?.role === 'Partner';
  const canDelete = (msg) => !msg.deleted_at && !msg._optimistic
    && (msg.sender_id === userId || isFirmLeader);

  const handleMembersAdded = (newUsers) => {
    const merged = [...localMembers, ...newUsers];
    setLocalMembers(merged);
    setShowAddMember(false);
    if (onMembersChanged) onMembersChanged();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><RefreshCw size={18} className="animate-spin text-slate-400" /></div>;
  }

  // Group messages by date, collapse consecutive same-sender runs
  const grouped = [];
  let lastDate = null;
  let lastSender = null;
  for (const msg of messages) {
    // Thread replies live in the side panel, not the main timeline.
    if (msg.parent_message_id) continue;
    const date = new Date(msg.created_at).toDateString();
    if (date !== lastDate) {
      grouped.push({ type: 'date', label: formatGroupDate(msg.created_at) });
      lastDate = date;
      lastSender = null;
    }
    const showSender = msg.sender_id !== lastSender;
    grouped.push({ type: 'msg', msg, showSender });
    lastSender = msg.sender_id;
  }

  return (
    // `relative` is load-bearing for mobile: ThreadPanel uses `absolute
    // inset-0` to overlay the chat column at narrow widths, and needs
    // this wrapper to anchor against. lg+ behaviour is unchanged.
    <div className="flex h-full relative">
      {/* Add member modal */}
      {showAddMember && channel.is_private && (
        <AddMemberModal
          firmId={firmId}
          channel={channel}
          currentMembers={localMembers}
          onAdded={handleMembersAdded}
          onClose={() => setShowAddMember(false)}
        />
      )}

      {/* Main chat column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          {channel.is_private
            ? <User size={16} className="text-slate-400 flex-shrink-0" />
            : <Hash size={16} className="text-slate-400 flex-shrink-0" />
          }
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                {channelLabel || channel.name}
              </p>
              {linkedMatter ? (
                <button
                  onClick={() => { window.location.hash = `#/matters/${linkedMatter.id}`; }}
                  title={`Linked matter — ${linkedMatter.matter_number || ''}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/30 transition max-w-[200px]"
                >
                  <Briefcase size={10} />
                  <span className="truncate">{linkedMatter.title}</span>
                </button>
              ) : null}
            </div>
            {!channel.is_private && channel.description && (
              <p className="text-xs text-slate-400 truncate">{channel.description}</p>
            )}
            {channel.is_private && (
              <p className="text-xs text-slate-400">{localMembers.length} members</p>
            )}
          </div>

          {/* Universal header actions (visible for every channel) */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setShowMatterPicker(true)}
              title={linkedMatter ? 'Change linked matter' : 'Link this channel to a matter'}
              className={`p-1.5 rounded-lg transition ${
                linkedMatter
                  ? 'text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/20 hover:bg-indigo-100 dark:hover:bg-indigo-500/30'
                  : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
              }`}
            >
              <Link2 size={15} />
            </button>
            <button
              onClick={handleExportThread}
              title="Export conversation to PDF"
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <Printer size={15} />
            </button>
            {isFirmLeader && (
              <button
                onClick={async () => {
                  const next = !channel.is_encrypted;
                  if (next && !enc.configured) {
                    toast.error('Set up end-to-end encryption in Settings → Security first.');
                    return;
                  }
                  if (!window.confirm(next
                    ? 'Mark this channel as end-to-end encrypted? New messages will only be readable by staff who have unlocked the firm passphrase.'
                    : 'Disable encryption for this channel? Future messages will be stored in plaintext. Existing encrypted messages will still need an unlock to read.')) return;
                  const { error: err } = await setChannelEncryption(channel.id, next);
                  if (err) { toast.error(err.message || 'Could not update'); return; }
                  // Reflect locally so the badge updates immediately.
                  // eslint-disable-next-line no-param-reassign
                  channel.is_encrypted = next;
                  toast.success(next ? 'Channel is now end-to-end encrypted.' : 'Encryption disabled on this channel.');
                  setDecryptedById({}); // force a re-decrypt pass
                }}
                title={channel.is_encrypted ? 'Disable end-to-end encryption' : 'Mark channel as end-to-end encrypted'}
                className={`p-1.5 rounded-lg transition ${
                  channel.is_encrypted
                    ? 'text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/20 hover:bg-emerald-100 dark:hover:bg-emerald-500/30'
                    : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                }`}
              >
                {channel.is_encrypted ? <Lock size={15} /> : <Unlock size={15} />}
              </button>
            )}
            {channel.is_encrypted && !enc.unlocked && (
              <span className="text-[10px] text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/20 inline-flex items-center gap-1">
                🔒 Locked
              </span>
            )}
            {isFirmLeader && !channel.archived_at && (
              <button
                onClick={handleArchiveChannel}
                title="Archive channel"
                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition"
              >
                <Archive size={15} />
              </button>
            )}
          </div>

          {/* Private-channel-specific buttons (members, etc.) */}
          {channel.is_private && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Show member avatars (up to 3) */}
              <div className="flex items-center -space-x-1.5 mr-1">
                {localMembers.slice(0, 3).map((m) => {
                  const n = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '?';
                  const ini = n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                  return (
                    <div key={m.id} title={n}
                      className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold">
                      {ini}
                    </div>
                  );
                })}
                {localMembers.length > 3 && (
                  <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold">
                    +{localMembers.length - 3}
                  </div>
                )}
              </div>

              {/* Add member */}
              <button
                onClick={() => setShowAddMember(true)}
                title="Add people"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                <UserPlus size={15} />
              </button>

              {/* Toggle members panel */}
              <button
                onClick={() => setShowMembers((v) => !v)}
                title={showMembers ? 'Hide members' : 'Show members'}
                className={`p-1.5 rounded-lg transition
                  ${showMembers
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                <Users size={15} />
              </button>
            </div>
          )}
        </div>

      {/* Pinned messages strip — collapsible, only renders when there are pins. */}
      {pinned.length > 0 && (
        <div className="border-b border-slate-100 dark:border-slate-800 bg-amber-50/40 dark:bg-amber-900/10">
          <button
            type="button"
            onClick={() => setPinnedOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-5 py-2 text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition"
          >
            <Bookmark size={11} fill="currentColor" />
            {pinned.length} pinned message{pinned.length === 1 ? '' : 's'}
            <ChevronDown size={11} className={`ml-auto transition-transform ${pinnedOpen ? 'rotate-180' : ''}`} />
          </button>
          {pinnedOpen && (
            <ul className="px-5 pb-2 space-y-1.5 max-h-40 overflow-y-auto">
              {pinned.map((p) => {
                const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.email || 'Unknown';
                return (
                  <li key={p.id} className="flex items-start gap-2 text-xs">
                    <span className="text-amber-600 dark:text-amber-400 mt-0.5">📌</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-700 dark:text-slate-200">{name}: </span>
                      <span className="text-slate-600 dark:text-slate-300 line-clamp-2 break-words whitespace-pre-wrap">
                        {p.body || (p.attachments?.length ? `📎 ${p.attachments.length} attachment${p.attachments.length === 1 ? '' : 's'}` : '')}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5">
        {grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 py-12">
            <Hash size={32} className="mb-3 opacity-20" />
            <p className="text-sm font-medium">No messages yet in #{channel.name}</p>
            <p className="text-xs mt-1">Be the first to say something!</p>
          </div>
        ) : (
          grouped.map((item, i) => {
            if (item.type === 'date') {
              return (
                <div key={`date-${i}`} className="flex items-center gap-3 py-3">
                  <div className="flex-1 border-t border-slate-100 dark:border-slate-800" />
                  <span className="text-[11px] text-slate-400 font-medium">{item.label}</span>
                  <div className="flex-1 border-t border-slate-100 dark:border-slate-800" />
                </div>
              );
            }
            const { msg, showSender } = item;
            const isMe      = msg.sender_id === userId;
            const isEditing = editingId === msg.id;
            const sender    = msg.sender;
            const name = sender ? [sender.first_name, sender.last_name].filter(Boolean).join(' ') || sender.email : 'Unknown';
            const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

            return (
              <div key={msg.id} className={`flex gap-3 ${showSender ? 'mt-3' : 'mt-0.5'} group relative`}>
                {/* Avatar */}
                <div className="w-8 flex-shrink-0 mt-0.5">
                  {showSender && (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
                      ${isMe ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                      {ini}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {showSender && (
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className={`text-sm font-semibold ${isMe ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-900 dark:text-white'}`}>
                        {isMe ? 'You' : name}
                      </span>
                      <span className="text-[11px] text-slate-400">{formatTime(msg.created_at)}</span>
                      {msg.edited_at && (
                        <button
                          type="button"
                          onClick={() => setHistoryOf({ id: msg.id, body: msg.body })}
                          className="text-[11px] text-slate-400 italic hover:text-indigo-600 hover:underline transition"
                          title="View edit history"
                        >
                          (edited)
                        </button>
                      )}
                    </div>
                  )}

                  {/* Inline edit mode */}
                  {isEditing ? (
                    <div className="mt-0.5">
                      <textarea
                        ref={editRef}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(msg.id); }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        rows={Math.min(6, editBody.split('\n').length + 1)}
                        className="w-full rounded-xl border border-indigo-400 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white px-3.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      />
                      <div className="flex items-center gap-2 mt-1.5">
                        <button
                          onClick={() => saveEdit(msg.id)}
                          disabled={savingEdit || !editBody.trim()}
                          className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium transition"
                        >
                          <Check size={12} />
                          {savingEdit ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs transition"
                        >
                          <X size={12} /> Cancel
                        </button>
                        <span className="text-[11px] text-slate-400">Enter to save · Esc to cancel</span>
                      </div>
                    </div>
                  ) : msg.deleted_at ? (
                    <p className="text-sm italic text-slate-400">Message deleted</p>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        {(() => {
                          // Encryption-aware body resolution:
                          //   - If the body is an enc:v1 envelope and we have plaintext cached, render it.
                          //   - If it's an envelope and we don't (locked or wrong key), show a lock placeholder.
                          //   - Otherwise render the body as-is via the markdown renderer.
                          if (isEncryptedEnvelope(msg.body)) {
                            const plain = decryptedById[msg.id];
                            if (plain === undefined) {
                              return (
                                <p className="flex-1 text-sm italic text-slate-400">
                                  🔒 Encrypted — decrypting…
                                </p>
                              );
                            }
                            if (plain === null) {
                              return (
                                <p className="flex-1 text-sm italic text-amber-600 dark:text-amber-400">
                                  🔒 Encrypted — unlock encryption in Settings to read.
                                </p>
                              );
                            }
                            return (
                              <p className="flex-1 text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
                                <RichText body={plain} currentUserId={userId} />
                              </p>
                            );
                          }
                          return (
                            <p className="flex-1 text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
                              <RichText body={msg.body} currentUserId={userId} />
                            </p>
                          );
                        })()}
                        <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                          {!msg._optimistic && bookmarks && (
                            <button
                              onClick={() => bookmarks.toggle({
                                ...msg,
                                channel: { name: channel.name, is_private: channel.is_private },
                              })}
                              className={`p-1 rounded transition ${
                                bookmarks.ids.has(msg.id)
                                  ? 'text-amber-500 hover:text-amber-600'
                                  : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                              }`}
                              title={bookmarks.ids.has(msg.id) ? 'Remove bookmark' : 'Save for later'}
                            >
                              <Bookmark size={13} fill={bookmarks.ids.has(msg.id) ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {!msg._optimistic && (
                            <button
                              onClick={() => setActiveThread(msg)}
                              className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition"
                              title="Reply in thread"
                            >
                              <MessageSquare size={13} />
                            </button>
                          )}
                          {!msg._optimistic && (
                            <button
                              onClick={async () => {
                                const { data: nowPinned } = await togglePinFirmMessage(msg.id);
                                setMessages((prev) => prev.map((m) =>
                                  m.id === msg.id ? { ...m, is_pinned: !!nowPinned } : m
                                ));
                                // Patch the pinned strip too so it updates without a refetch.
                                if (nowPinned) {
                                  setPinned((prev) => prev.some((p) => p.id === msg.id)
                                    ? prev
                                    : [{ id: msg.id, body: msg.body, attachments: msg.attachments,
                                        first_name: msg.sender?.first_name, last_name: msg.sender?.last_name,
                                        email: msg.sender?.email, pinned_at: new Date().toISOString(),
                                        created_at: msg.created_at }, ...prev]);
                                } else {
                                  setPinned((prev) => prev.filter((p) => p.id !== msg.id));
                                }
                              }}
                              className={`p-1 rounded transition ${
                                msg.is_pinned
                                  ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                                  : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                              }`}
                              title={msg.is_pinned ? 'Unpin' : 'Pin to channel'}
                            >
                              <Bookmark size={13} fill={msg.is_pinned ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {/* Edit — own messages only */}
                          {isMe && !msg._optimistic && msg.body && (
                            <button
                              onClick={() => startEdit(msg)}
                              className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                              title="Edit message"
                            >
                              <Pencil size={13} />
                            </button>
                          )}
                          {canDelete(msg) && (
                            <button
                              onClick={() => handleDelete(msg)}
                              disabled={deletingId === msg.id}
                              className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                              title="Delete message"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                      <MessageAttachments attachments={msg.attachments} />
                      {threadCounts[msg.id]?.reply_count > 0 && (
                        <button
                          onClick={() => setActiveThread(msg)}
                          className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
                        >
                          <MessageSquare size={11} />
                          {threadCounts[msg.id].reply_count} repl{threadCounts[msg.id].reply_count === 1 ? 'y' : 'ies'}
                          <ChevronDown size={10} className="opacity-60" />
                        </button>
                      )}
                      {!msg._optimistic && (
                        <MessageReactions
                          scope="firm"
                          messageId={msg.id}
                          firmId={firmId}
                          userId={userId}
                          reactions={reactionsByMsg.get(msg.id) || []}
                          onChange={refreshReactions}
                        />
                      )}
                    </>
                  )}

                  {/* Show (edited) on non-first lines in collapsed runs */}
                  {!showSender && msg.edited_at && !isEditing && !msg.deleted_at && (
                    <span className="text-[11px] text-slate-400 italic ml-0.5">(edited)</span>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <form onSubmit={send} className="border-t border-slate-100 dark:border-slate-800 p-3 relative">
        {typers.length > 0 && (
          <div className="px-2 pb-1 text-[11px] text-slate-500 italic flex items-center gap-1">
            <span className="inline-flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-slate-400 animate-pulse" />
              <span className="w-1 h-1 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '150ms' }} />
              <span className="w-1 h-1 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '300ms' }} />
            </span>
            {typers.length === 1
              ? `${typers[0].name} is typing…`
              : typers.length === 2
                ? `${typers[0].name} and ${typers[1].name} are typing…`
                : `${typers[0].name} and ${typers.length - 1} others are typing…`}
          </div>
        )}
        <PendingAttachments
          pending={pendingFiles.map((f) => ({ file: f, mime: f.type }))}
          onRemove={(idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx))}
        />
        {(() => {
          const hits = scanDlp(body, dlpSettings);
          if (!hits.length) return null;
          const hasBlock = hits.some((h) => h.severity === 'block');
          // Dedupe by rule so we don't list "credit_card" 4 times.
          const seen = new Set();
          const unique = hits.filter((h) => {
            if (seen.has(h.rule)) return false;
            seen.add(h.rule);
            return true;
          });
          return (
            <div
              className={`mb-2 px-3 py-2 rounded-lg text-xs border ${
                hasBlock
                  ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
                  : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
              }`}
            >
              <p className="font-medium mb-0.5">
                {hasBlock ? 'This message will be blocked' : 'Heads up: sensitive content detected'}
              </p>
              <ul className="space-y-0.5">
                {unique.map((h, i) => (
                  <li key={i} className="opacity-90">
                    • {DLP_RULE_LABELS[h.rule] || h.rule}
                    <span className="font-mono ml-1 opacity-75">({h.match})</span>
                    {h.severity === 'warn' && <span className="ml-1">— will send but logged for review</span>}
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
        <div className="flex items-end gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition relative">
          <AttachButton
            disabled={sending}
            onSelect={(files) => setPendingFiles((cur) => [...cur, ...files])}
          />
          <ScheduleSendMenu
            scheduledFor={scheduledFor}
            onChange={setScheduledFor}
            disabled={sending}
          />
          <textarea
            ref={composerRef}
            value={body}
            onChange={(e) => { setBody(e.target.value); if (e.target.value) notifyTyping(); }}
            onSelect={mention.onSelectionChange}
            placeholder={`Message ${channel.is_private ? channelLabel || channel.name : '#' + channel.name}  ·  type @ to mention · ${FORMATTING_HINT}`}
            rows={Math.min(5, Math.max(1, body.split('\n').length))}
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none resize-none py-1"
            onKeyDown={(e) => {
              if (mention.onKeyDown(e)) return;
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); }
            }}
          />
          <button
            type="submit"
            disabled={(!body.trim() && pendingFiles.length === 0) || sending}
            title={scheduledFor ? `Schedule for ${new Date(scheduledFor).toLocaleString()}` : 'Send'}
            className={`flex-shrink-0 px-2 py-1.5 rounded-lg disabled:opacity-40 text-white transition inline-flex items-center gap-1 ${
              scheduledFor
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {scheduledFor ? <Clock size={13} /> : <Send size={13} />}
            {scheduledFor && <span className="text-[11px] font-medium">Schedule</span>}
          </button>
          {/* Mention picker floats above the composer */}
          <div className="absolute left-12 bottom-full">{mention.element}</div>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5 px-1">Press Enter to send · Shift+Enter for new line · @ to mention</p>
      </form>
      </div>{/* end main chat column */}

      {/* Thread side panel — visible when a parent message is selected */}
      {activeThread && (
        <ThreadPanel
          parent={activeThread}
          firmId={firmId}
          channelId={channel.id}
          userId={userId}
          userProfile={userProfile}
          onClose={() => setActiveThread(null)}
          onChange={() => {
            setThreadCounts((prev) => ({
              ...prev,
              [activeThread.id]: {
                reply_count: (prev[activeThread.id]?.reply_count || 0) + 1,
                last_reply_at: new Date().toISOString(),
              },
            }));
          }}
        />
      )}

      {/* Edit history modal */}
      {historyOf && (
        <EditHistoryModal
          messageId={historyOf.id}
          currentBody={historyOf.body}
          onClose={() => setHistoryOf(null)}
        />
      )}

      {/* Matter picker */}
      {showMatterPicker && (
        <MatterPickerModal
          firmId={firmId}
          currentMatterId={linkedMatter?.id || null}
          onPick={async (matterId) => {
            const { error } = await setChannelMatter(channel.id, matterId);
            if (error) { toast.error('Could not link: ' + error.message); return; }
            if (!matterId) {
              setLinkedMatter(null);
              toast.success('Channel unlinked from matter.');
            } else {
              const { data } = await fetchMatterSummary(matterId);
              setLinkedMatter(data || null);
              toast.success(`Linked to "${data?.title || 'matter'}"`);
            }
            setShowMatterPicker(false);
          }}
          onClose={() => setShowMatterPicker(false)}
        />
      )}

      {/* Members panel */}
      {showMembers && channel.is_private && (
        <div className="flex-shrink-0 w-52 border-l border-slate-100 dark:border-slate-800 flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Members · {localMembers.length}
            </span>
            <button
              onClick={() => setShowAddMember(true)}
              title="Add people"
              className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
            >
              <UserPlus size={13} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
            {localMembers.length === 0 && (
              <p className="text-xs text-slate-400 px-2 py-2">No members yet.</p>
            )}
            {localMembers.map((m) => {
              const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'Unknown';
              const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
              const isMe = m.id === userId;
              return (
                <div key={m.id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0
                    ${isMe ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                           : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                    {ini}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">
                      {isMe ? 'You' : name}
                    </p>
                    {m.email && !isMe && (
                      <p className="text-[10px] text-slate-400 truncate">{m.email}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-3 py-2.5 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setShowAddMember(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
            >
              <UserPlus size={12} /> Add people
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New DM / Group chat modal ───────────────────────────────────────────────
function NewDMModal({ firmId, userId, onCreated, onClose }) {
  const [allUsers,  setAllUsers]  = useState([]);
  const [selected,  setSelected]  = useState([]);
  const [creating,  setCreating]  = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    listFirmUsers(firmId).then(({ data }) => {
      setAllUsers((data || []).filter((u) => u.id !== userId));
    });
  }, [firmId, userId]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const create = async () => {
    if (!selected.length) return;
    setCreating(true);
    // Always include the current user in the DM
    const memberIds = [...new Set([userId, ...selected])];
    const { data, error: err } = await createPrivateChannel(firmId, memberIds, null, userId);
    setCreating(false);
    if (err) { setError(err.message); return; }
    onCreated(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">New direct message</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">Select one or more colleagues to start a private chat.</p>

        <div className="space-y-1 max-h-60 overflow-y-auto">
          {allUsers.length === 0 && <p className="text-xs text-slate-400">No other team members found.</p>}
          {allUsers.map((u) => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
            const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
            const sel  = selected.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition
                  ${sel ? 'bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0
                  ${sel ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                  {ini}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.role || u.email}</p>
                </div>
                {sel && <Check size={15} className="text-indigo-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button
            onClick={create}
            disabled={!selected.length || creating}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
          >
            {creating ? 'Opening…' : selected.length > 1 ? 'Start group chat' : 'Start chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamChat({ firmId, userId, userProfile, pendingHighlight, onHighlightApplied, bookmarks }) {
  const [channels,      setChannels]      = useState([]);
  const [dms,           setDms]           = useState([]);
  const [activeId,      setActiveId]      = useState(null);
  const [showNew,       setShowNew]       = useState(false);
  const [showNewDM,     setShowNewDM]     = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [showThread,    setShowThread]    = useState(false);
  const [unreadCounts,  setUnreadCounts]  = useState({}); // { [channelId]: number }
  const [sidebarQuery,  setSidebarQuery]  = useState('');
  const activeIdRef = useRef(activeId);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // Search overlay handed us a channel to focus on.
  useEffect(() => {
    if (!pendingHighlight || pendingHighlight.scope !== 'firm') return;
    setActiveId(pendingHighlight.container_id);
    setShowThread(true);
    onHighlightApplied?.();
  }, [pendingHighlight, onHighlightApplied]);

  const loadUnread = useCallback(async () => {
    if (!userId) return;
    const { data } = await getChannelUnreadCounts(userId);
    if (data) {
      const map = {};
      for (const row of data) map[row.channel_id] = Number(row.unread_count);
      setUnreadCounts(map);
    }
  }, [userId]);

  const loadAll = useCallback(async () => {
    if (!firmId || !userId) return;
    const [chRes, dmRes] = await Promise.all([
      listFirmChannels(firmId),
      listFirmPrivateChannels(firmId, userId),
    ]);
    const ch = chRes.data || [];
    setChannels(ch);
    setDms(dmRes.data || []);
    setLoading(false);
    if (ch.length && !activeId) {
      setActiveId(ch.find((c) => c.name === 'general')?.id || ch[0].id);
    }
    loadUnread();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId, userId, loadUnread]);

  useEffect(() => {
    loadAll();

    if (!userId) return;
    // Realtime: increment unread badge when a colleague posts to any channel
    const sub = supabase
      .channel(`unread_watch_${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'firm_messages',
      }, (payload) => {
        const msg = payload.new;
        if (msg.sender_id === userId) return; // own messages never unread
        if (msg.channel_id === activeIdRef.current) return; // currently open
        setUnreadCounts((prev) => ({
          ...prev,
          [msg.channel_id]: (prev[msg.channel_id] || 0) + 1,
        }));
      })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, [loadAll, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called by TeamChatThread when it marks a channel read
  const handleChannelRead = useCallback((channelId) => {
    setUnreadCounts((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  }, []);

  const allChannels = [...channels, ...dms];
  const activeChannel = allChannels.find((c) => c.id === activeId);

  const handleChannelCreated = (ch) => {
    setChannels((prev) => [...prev, ch].sort((a, b) => a.name.localeCompare(b.name)));
    setActiveId(ch.id);
    setShowNew(false);
    setShowThread(true);
  };

  const handleDMCreated = (ch) => {
    setDms((prev) => {
      const exists = prev.find((d) => d.id === ch.id);
      return exists ? prev : [ch, ...prev];
    });
    setActiveId(ch.id);
    setShowNewDM(false);
    setShowThread(true);
  };

  // Display name for a DM: other member's name (1:1) or comma-separated (group)
  const dmLabel = (ch) => {
    const others = (ch.members || []).filter((m) => m.id !== userId);
    if (!others.length) return 'Direct message';
    return others.map((m) => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email).join(', ');
  };

  const SidebarItem = ({ ch, icon: Icon, label }) => {
    const unread = unreadCounts[ch.id] || 0;
    const isActive = activeId === ch.id;
    return (
      <button
        onClick={() => { setActiveId(ch.id); setShowThread(true); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition rounded-lg
          ${isActive
            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
            : unread > 0
              ? 'text-slate-900 dark:text-white font-semibold hover:bg-slate-50 dark:hover:bg-slate-800/60'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/60'
          }`}
      >
        <Icon size={14} className="flex-shrink-0 opacity-60" />
        <span className="flex-1 truncate">{label}</span>
        {!isActive && unread > 0 && (
          <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex h-full">
      {showNew   && <NewChannelModal firmId={firmId} userId={userId} onCreated={handleChannelCreated} onClose={() => setShowNew(false)} />}
      {showNewDM && <NewDMModal     firmId={firmId} userId={userId} onCreated={handleDMCreated}      onClose={() => setShowNewDM(false)} />}

      {/* Sidebar */}
      <div className={`flex-shrink-0 border-r border-slate-100 dark:border-slate-800 flex flex-col
        ${showThread ? 'hidden md:flex' : 'flex'} w-full md:w-60 lg:w-68`}>

        {/* Search row */}
        <div className="px-3 pt-3 pb-2 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={sidebarQuery}
              onChange={(e) => setSidebarQuery(e.target.value)}
              placeholder="Search channels & DMs"
              className="w-full pl-7 pr-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-16"><RefreshCw size={14} className="animate-spin text-slate-400" /></div>
          ) : (
            <>
              {/* Channels section */}
              <div>
                <div className="flex items-center justify-between px-2 mb-1">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Channels</span>
                  <button onClick={() => setShowNew(true)} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition" title="New channel">
                    <Plus size={13} />
                  </button>
                </div>
                {(() => {
                  const q = sidebarQuery.trim().toLowerCase();
                  const filtered = q
                    ? channels.filter((ch) => (ch.name || '').toLowerCase().includes(q))
                    : channels;
                  if (channels.length === 0) {
                    return <p className="text-xs text-slate-400 px-2 py-1">No channels yet.</p>;
                  }
                  if (filtered.length === 0) {
                    return <p className="text-xs text-slate-400 px-2 py-1">No matches.</p>;
                  }
                  return filtered.map((ch) => <SidebarItem key={ch.id} ch={ch} icon={Hash} label={ch.name} />);
                })()}
              </div>

              {/* Direct Messages section */}
              <div>
                <div className="flex items-center justify-between px-2 mb-1">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Direct Messages</span>
                  <button onClick={() => setShowNewDM(true)} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition" title="New DM">
                    <UserPlus size={13} />
                  </button>
                </div>
                {(() => {
                  const q = sidebarQuery.trim().toLowerCase();
                  const visible = q
                    ? dms.filter((ch) => dmLabel(ch).toLowerCase().includes(q))
                    : dms;
                  if (dms.length === 0) {
                    return <p className="text-xs text-slate-400 px-2 py-1">No direct messages yet.</p>;
                  }
                  if (visible.length === 0) {
                    return <p className="text-xs text-slate-400 px-2 py-1">No matches.</p>;
                  }
                  return visible.map((ch) => {
                    const others = (ch.members || []).filter((m) => m.id !== userId);
                    const isGroup = others.length > 1;
                    return <SidebarItem key={ch.id} ch={ch} icon={isGroup ? Users : User} label={dmLabel(ch)} />;
                  });
                })()}
              </div>
            </>
          )}
        </div>

        {/* Current user footer */}
        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-700 dark:text-indigo-300 text-xs font-semibold flex-shrink-0">
            {initials(userProfile)}
          </div>
          <span className="text-xs text-slate-600 dark:text-slate-400 truncate font-medium">
            {[userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || userProfile?.email || 'You'}
          </span>
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex flex-col min-w-0 ${showThread ? 'flex' : 'hidden md:flex'}`}>
        {activeChannel ? (
          <>
            <button onClick={() => setShowThread(false)} className="md:hidden flex items-center gap-1.5 text-sm text-indigo-600 px-4 pt-3 pb-0">
              <ChevronLeft size={16} /> Back
            </button>
            <TeamChatThread
              key={activeChannel.id}
              channel={activeChannel}
              firmId={firmId}
              userId={userId}
              userProfile={userProfile}
              channelLabel={activeChannel.is_private ? dmLabel(activeChannel) : null}
              onMembersChanged={loadAll}
              onChannelRead={handleChannelRead}
              bookmarks={bookmarks}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <Hash size={40} className="mb-3 opacity-20" />
            <p className="text-sm">Select a channel or start a direct message</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Client Portal Messages ──────────────────────────────────────────────────

// Modal to start a new conversation: pick a client + optionally add firm colleagues
function NewClientMessageModal({ firmId, userId, onStart, onClose }) {
  const [contacts,     setContacts]     = useState([]);
  const [firmUsers,    setFirmUsers]    = useState([]);
  const [search,       setSearch]       = useState('');
  const [selContact,   setSelContact]   = useState(null);
  const [selUsers,     setSelUsers]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [creating,     setCreating]     = useState(false);

  useEffect(() => {
    Promise.all([
      listContacts(firmId),
      listFirmUsers(firmId),
    ]).then(([cRes, uRes]) => {
      setContacts(cRes.data || []);
      setFirmUsers((uRes.data || []).filter((u) => u.id !== userId));
      setLoading(false);
    });
  }, [firmId, userId]);

  const toggleUser = (id) =>
    setSelUsers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase();
    return (
      contactName(c).toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  const start = async () => {
    if (!selContact) return;
    setCreating(true);
    // Always add the current user as a participant
    const participantIds = [...new Set([userId, ...selUsers])];
    await Promise.all(participantIds.map((uid) => addThreadParticipant(firmId, selContact.id, uid)));
    setCreating(false);
    onStart(selContact, participantIds);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between flex-shrink-0">
          <h3 className="font-semibold text-slate-900 dark:text-white">New client message</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {/* Client picker */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Client</label>
          {selContact ? (
            <div className="flex items-center gap-2.5 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl border border-indigo-200 dark:border-indigo-800">
              <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {initials(selContact)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{contactName(selContact)}</p>
                {selContact.email && <p className="text-xs text-slate-400 truncate">{selContact.email}</p>}
              </div>
              <button onClick={() => setSelContact(null)} className="p-1 text-slate-400 hover:text-red-500 transition"><X size={14} /></button>
            </div>
          ) : (
            <div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients…"
                className="w-full px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-1.5"
                autoFocus
              />
              {loading ? (
                <div className="flex justify-center py-4"><RefreshCw size={14} className="animate-spin text-slate-400" /></div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-0.5 border border-slate-100 dark:border-slate-800 rounded-xl">
                  {filtered.length === 0 && <p className="text-xs text-slate-400 px-3 py-3">No clients found.</p>}
                  {filtered.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelContact(c); setSearch(''); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 text-left transition"
                    >
                      <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {initials(c)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 dark:text-white truncate">{contactName(c)}</p>
                        {c.email && <p className="text-xs text-slate-400 truncate">{c.email}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Firm participants picker */}
        {firmUsers.length > 0 && (
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Add team members <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <div className="max-h-36 overflow-y-auto space-y-0.5 border border-slate-100 dark:border-slate-800 rounded-xl">
              {firmUsers.map((u) => {
                const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                const sel  = selUsers.includes(u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => toggleUser(u.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition
                      ${sel ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0
                      ${sel ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                      {ini}
                    </div>
                    <span className="flex-1 text-sm text-slate-800 dark:text-slate-200 truncate">{name}</span>
                    {sel && <Check size={13} className="text-indigo-600 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-1 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button
            onClick={start}
            disabled={!selContact || creating}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition"
          >
            {creating ? 'Opening…' : 'Open conversation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConversationList({ conversations, activeId, onSelect, loading, onNew }) {
  if (loading) {
    return <div className="flex items-center justify-center h-32"><RefreshCw size={18} className="animate-spin text-slate-400" /></div>;
  }
  if (!conversations.length) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-400 p-6 text-center">
        <MessageSquare size={32} className="mb-3 opacity-30" />
        <p className="text-sm">No messages yet.</p>
        <p className="text-xs mt-1">Click "New Message" to start a conversation with a client.</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
      {conversations.map(({ contact, lastMessage, unreadCount }) => (
        <li key={contact.id}>
          <button
            onClick={() => onSelect(contact.id)}
            className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800/60
              ${activeId === contact.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
          >
            <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0 text-indigo-700 dark:text-indigo-300 font-semibold text-xs">
              {initials(contact)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-sm font-medium text-slate-900 dark:text-white truncate flex-1">{contactName(contact)}</span>
                {contact.portal_thread_private && (
                  <Lock size={11} className="text-amber-500 flex-shrink-0" aria-label="Private thread" />
                )}
                <span className="text-[11px] text-slate-400 flex-shrink-0">{formatTime(lastMessage?.created_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-xs truncate ${unreadCount > 0 ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                  {lastMessage ? (lastMessage.sender_type === 'firm' ? 'You: ' : '') + (lastMessage.body || '📎 Attachment') : 'No messages yet'}
                </p>
                {unreadCount > 0 && (
                  <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// Modal to add a firm member to an existing client thread
function AddThreadMemberModal({ firmId, userId, contactId, currentParticipants, onAdded, onClose }) {
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [adding,   setAdding]   = useState(false);
  const [error,    setError]    = useState('');

  useEffect(() => {
    listFirmUsers(firmId).then(({ data }) => {
      const existing = new Set((currentParticipants || []).map((p) => p.user_id || p.id));
      setAllUsers((data || []).filter((u) => !existing.has(u.id)));
    });
  }, [firmId, currentParticipants]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const add = async () => {
    if (!selected.length) return;
    setAdding(true);
    const errs = [];
    const added = [];
    for (const uid of selected) {
      const { error: err } = await addThreadParticipant(firmId, contactId, uid);
      if (err) errs.push(err.message);
      else {
        const u = allUsers.find((x) => x.id === uid);
        if (u) added.push({ user_id: uid, user: u });
      }
    }
    setAdding(false);
    if (errs.length) { setError(errs.join('; ')); return; }
    onAdded(added);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">Add team members</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-xs text-slate-500">Added members can see all messages in this conversation.</p>
        <div className="space-y-0.5 max-h-60 overflow-y-auto">
          {allUsers.length === 0 && <p className="text-xs text-slate-400 py-2 text-center">All team members are already in this thread.</p>}
          {allUsers.map((u) => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
            const ini  = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
            const sel  = selected.includes(u.id);
            return (
              <button key={u.id} onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition
                  ${sel ? 'bg-indigo-50 dark:bg-indigo-900/30 ring-1 ring-indigo-300' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0
                  ${sel ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>{ini}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.role || u.email}</p>
                </div>
                {sel && <Check size={15} className="text-indigo-600 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
          <button onClick={add} disabled={!selected.length || adding || allUsers.length === 0}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium transition">
            {adding ? 'Adding…' : `Add ${selected.length > 0 ? selected.length : ''} ${selected.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PortalThread({ contactId, conversations, firmId, userId, userProfile, onMessagesRead, onPrivacyChanged }) {
  const [messages,       setMessages]       = useState([]);
  const [body,           setBody]           = useState('');
  const [loading,        setLoading]        = useState(true);
  const [sending,        setSending]        = useState(false);
  const [participants,   setParticipants]   = useState([]);
  const [showPanel,      setShowPanel]      = useState(false);
  const [showAddMember,  setShowAddMember]  = useState(false);
  const [pendingFiles,   setPendingFiles]   = useState([]);
  const [deletingId,     setDeletingId]     = useState(null);
  // Local mirror of the contact's privacy flag so toggling shows up
  // immediately without waiting for a parent refresh.
  const [isPrivate,      setIsPrivate]      = useState(false);
  const [privacyBusy,    setPrivacyBusy]    = useState(false);
  const bottomRef = useRef(null);

  const conv    = conversations.find((c) => c.contact.id === contactId);
  const contact = conv?.contact;

  useEffect(() => {
    setIsPrivate(!!contact?.portal_thread_private);
  }, [contact?.portal_thread_private]);

  const isFirmLeader = ['Admin','Owner','Partner'].includes(userProfile?.role || '');

  const portalMsgIds = messages.map((m) => m.id).filter((id) => !String(id).startsWith('temp-'));
  const { byMsg: reactionsByMsg, refresh: refreshReactions } = useMessageReactions('portal', portalMsgIds);

  useEffect(() => {
    let isMounted = true;

    // ── Initial load ──────────────────────────────────────────────────────────
    setLoading(true);
    setMessages([]);
    setParticipants([]);
    setShowPanel(false);

    listFirmPortalMessages(contactId).then(async ({ data }) => {
      if (!isMounted) return;
      setMessages(data || []);
      setLoading(false);
      await markFirmMessagesRead(contactId);
      onMessagesRead(contactId);
    });

    listThreadParticipants(contactId).then(({ data }) => {
      if (!isMounted) return;
      setParticipants(data || []);
    });

    // ── Realtime subscription ────────────────────────────────────────────────
    const sub = supabase
      .channel(`portal_msgs_${contactId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'portal_messages',
        filter: `contact_id=eq.${contactId}`,
      }, async (payload) => {
        const msg = payload.new;
        if (!isMounted) return;
        // Skip own firm messages (already added optimistically)
        if (msg.sender_type === 'firm' && msg.sender_id === userId) return;
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        await markFirmMessagesRead(contactId);
        onMessagesRead(contactId);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'portal_messages',
        filter: `contact_id=eq.${contactId}`,
      }, (payload) => {
        const msg = payload.new;
        if (!isMounted) return;
        setMessages((prev) => prev.map((m) => m.id === msg.id
          ? { ...m, body: msg.body, attachments: msg.attachments, deleted_at: msg.deleted_at, read_at: msg.read_at }
          : m
        ));
      })
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(sub);
    };
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    const files = pendingFiles;
    if (!text && files.length === 0) return;
    setSending(true);

    const msgId = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const temp = {
      id: msgId, body: text || null, sender_type: 'firm', sender_id: userId,
      created_at: new Date().toISOString(), attachments: [], _optimistic: true,
    };
    setMessages((prev) => [...prev, temp]);
    setBody('');
    setPendingFiles([]);

    const attachments = [];
    for (const file of files) {
      const { data: a, error: upErr } = await uploadMessageAttachment(
        'portal',
        { firmId, contactId, messageId: msgId },
        file,
      );
      if (upErr) {
        toast.error(`Upload failed (${file.name}): ${upErr.message}`);
        setSending(false);
        setMessages((prev) => prev.filter((m) => m.id !== msgId));
        setBody(text);
        setPendingFiles(files);
        return;
      }
      attachments.push(a);
    }

    const { data, error } = await sendPortalMessage(firmId, contactId, userId, 'firm', {
      id: msgId,
      body: text || null,
      attachments,
    });
    setSending(false);
    if (error) {
      toast.error('Failed to send: ' + error.message);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setBody(text);
      setPendingFiles(files);
    } else {
      setMessages((prev) => prev.map((m) => m.id === msgId ? data : m));
    }
  };

  const handleDeletePortal = async (msg) => {
    if (!window.confirm('Delete this message for everyone in the thread?')) return;
    setDeletingId(msg.id);
    const { error } = await softDeletePortalMessage(msg.id);
    setDeletingId(null);
    if (error) { toast.error('Could not delete: ' + error.message); return; }
    setMessages((prev) => prev.map((m) =>
      m.id === msg.id ? { ...m, deleted_at: new Date().toISOString(), body: null, attachments: [] } : m
    ));
  };

  const togglePrivacy = async () => {
    const next = !isPrivate;
    const msg = next
      ? `Make this thread private?\n\nOnly Admins, Partners, and assigned team members will be able to see the conversation. You'll be added as a participant automatically.`
      : `Unlock this thread?\n\nEvery firm member will be able to see the conversation again.`;
    if (!window.confirm(msg)) return;
    setPrivacyBusy(true);
    const { data, error } = await setPortalThreadPrivacy(contactId, next);
    setPrivacyBusy(false);
    if (error) { toast.error('Could not update privacy: ' + error.message); return; }
    const updated = data === undefined ? next : data;
    setIsPrivate(updated);
    // If we just turned privacy on, ensure caller appears in the participants
    // panel without waiting for a refetch.
    if (updated && !participants.some((p) => (p.user_id || p.user?.id) === userId)) {
      setParticipants((prev) => [
        ...prev,
        { user_id: userId, user: { id: userId, first_name: userProfile?.first_name, last_name: userProfile?.last_name, email: userProfile?.email } },
      ]);
    }
    onPrivacyChanged?.(contactId, updated);
    toast.success(updated ? 'Thread locked — only assigned staff can see it.' : 'Thread unlocked — everyone in the firm can see it.');
  };

  if (loading) return <div className="flex items-center justify-center h-full"><RefreshCw size={18} className="animate-spin text-slate-400" /></div>;

  const participantUsers = participants.map((p) => p.user).filter(Boolean);

  return (
    <div className="flex h-full">
      {showAddMember && (
        <AddThreadMemberModal
          firmId={firmId}
          userId={userId}
          contactId={contactId}
          currentParticipants={participants}
          onAdded={(added) => { setParticipants((prev) => [...prev, ...added]); setShowAddMember(false); }}
          onClose={() => setShowAddMember(false)}
        />
      )}

      {/* Main thread column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-semibold text-xs flex-shrink-0">
            {initials(contact)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{contactName(contact)}</p>
              {isPrivate && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                  <Lock size={9} /> Private
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 truncate">{contact?.email || 'Client'}</p>
          </div>

          {/* Participant avatars + controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Privacy toggle — only firm leaders or current participants can flip */}
            {(isFirmLeader || participants.some((p) => (p.user_id || p.user?.id) === userId) || !isPrivate) && (
              <button
                onClick={togglePrivacy}
                disabled={privacyBusy}
                title={isPrivate ? 'Unlock thread — visible to all firm members' : 'Lock thread — only assigned staff can see it'}
                className={`p-1.5 rounded-lg transition disabled:opacity-50 ${
                  isPrivate
                    ? 'bg-amber-50 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/30'
                    : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/20'
                }`}
              >
                {isPrivate ? <Lock size={15} /> : <Unlock size={15} />}
              </button>
            )}
            {participantUsers.length > 0 && (
              <div className="flex items-center -space-x-1.5 mr-1">
                {participantUsers.slice(0, 3).map((u) => {
                  const n   = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '?';
                  const ini = n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                  return (
                    <div key={u.id} title={n}
                      className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold">
                      {ini}
                    </div>
                  );
                })}
                {participantUsers.length > 3 && (
                  <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold text-slate-500">
                    +{participantUsers.length - 3}
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setShowAddMember(true)} title="Add team member"
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
              <UserPlus size={15} />
            </button>
            <button onClick={() => setShowPanel((v) => !v)} title={showPanel ? 'Hide participants' : 'Show participants'}
              className={`p-1.5 rounded-lg transition ${showPanel
                ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
              <Users size={15} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 py-12">
              <MessageSquare size={32} className="mb-3 opacity-30" />
              <p className="text-sm">No messages yet.</p>
              <p className="text-xs mt-1">Send the first message below.</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isFirm    = msg.sender_type === 'firm';
              const isOwn     = isFirm && msg.sender_id === userId;
              const canDelete = !msg.deleted_at && !msg._optimistic && isOwn;
              return (
                <div key={msg.id} className={`flex ${isFirm ? 'justify-end' : 'justify-start'} group`}>
                  <div className={`flex flex-col gap-1 max-w-[72%] ${isFirm ? 'items-end' : 'items-start'}`}>
                    {!isFirm && (
                      <span className="text-xs font-medium text-slate-500 px-1">{contactName(contact)}</span>
                    )}
                    {msg.deleted_at ? (
                      <div className="px-4 py-2 rounded-2xl text-xs italic bg-slate-100 dark:bg-slate-800 text-slate-400">
                        Message deleted
                      </div>
                    ) : (
                      <div className="flex items-end gap-1.5">
                        {canDelete && (
                          <button
                            onClick={() => handleDeletePortal(msg)}
                            disabled={deletingId === msg.id}
                            title="Delete message"
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        <div className={`flex flex-col ${isFirm ? 'items-end' : 'items-start'}`}>
                          {(msg.body || msg.body === '') && (
                            <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap
                              ${isFirm ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-sm'}`}>
                              {msg.body}
                            </div>
                          )}
                          <MessageAttachments attachments={msg.attachments} />
                        </div>
                      </div>
                    )}
                    {!msg.deleted_at && !msg._optimistic && (
                      <MessageReactions
                        scope="portal"
                        messageId={msg.id}
                        firmId={firmId}
                        userId={userId}
                        reactions={reactionsByMsg.get(msg.id) || []}
                        onChange={refreshReactions}
                      />
                    )}
                    <span className="text-[11px] text-slate-400 px-1">{formatTime(msg.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Compose */}
        <form onSubmit={send} className="border-t border-slate-100 dark:border-slate-800 p-3">
          <PendingAttachments
            pending={pendingFiles.map((f) => ({ file: f, mime: f.type }))}
            onRemove={(idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx))}
          />
          <div className="flex gap-2 items-center">
            <AttachButton
              disabled={sending}
              onSelect={(files) => setPendingFiles((cur) => [...cur, ...files])}
            />
            <input
              value={body} onChange={(e) => setBody(e.target.value)}
              placeholder={`Message ${contactName(contact)}…`}
              className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3.5 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
            />
            <button type="submit" disabled={(!body.trim() && pendingFiles.length === 0) || sending}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition">
              <Send size={14} /><span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </form>
      </div>

      {/* Participants panel */}
      {showPanel && (
        <div className="flex-shrink-0 w-52 border-l border-slate-100 dark:border-slate-800 flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Team · {participantUsers.length}
            </span>
            <button onClick={() => setShowAddMember(true)} title="Add team member"
              className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition">
              <UserPlus size={13} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
            {participantUsers.length === 0 ? (
              <p className="text-xs text-slate-400 px-2 py-2">No team members assigned yet.</p>
            ) : (
              participantUsers.map((u) => {
                const name  = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'Unknown';
                const ini   = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                const isMe  = u.id === userId;
                return (
                  <div key={u.id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0
                      ${isMe ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                             : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                      {ini}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{isMe ? 'You' : name}</p>
                      {u.email && !isMe && <p className="text-[10px] text-slate-400 truncate">{u.email}</p>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="px-3 py-2.5 border-t border-slate-100 dark:border-slate-800">
            <button onClick={() => setShowAddMember(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition">
              <UserPlus size={12} /> Add team member
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientMessages({ firmId, userId, userProfile, pendingHighlight, onHighlightApplied }) {
  const [conversations, setConversations] = useState([]);
  const [activeId,      setActiveId]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [showThread,    setShowThread]    = useState(false);
  const [showNew,       setShowNew]       = useState(false);
  const [query,         setQuery]         = useState('');
  const [filterUnread,  setFilterUnread]  = useState(false);

  const load = useCallback(async () => {
    if (!firmId) return;
    const { data } = await listFirmConversations(firmId);
    setConversations(data || []);
    setLoading(false);
  }, [firmId]);

  // Search overlay handed us a portal thread to focus on.
  useEffect(() => {
    if (!pendingHighlight || pendingHighlight.scope !== 'portal') return;
    setActiveId(pendingHighlight.container_id);
    setShowThread(true);
    onHighlightApplied?.();
  }, [pendingHighlight, onHighlightApplied]);

  useEffect(() => {
    load();

    if (!firmId) return;
    // Realtime: refresh conversation list when any portal message is inserted
    const sub = supabase
      .channel(`portal_convos_${firmId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'portal_messages',
        filter: `firm_id=eq.${firmId}`,
      }, () => { load(); })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, [load, firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMessagesRead = useCallback((cid) => {
    setConversations((prev) => prev.map((c) => c.contact.id === cid ? { ...c, unreadCount: 0 } : c));
  }, []);

  // Patch the local conversation list in place so the lock badge updates
  // immediately when privacy is toggled inside a thread.
  const handlePrivacyChanged = useCallback((cid, isPrivate) => {
    setConversations((prev) => prev.map((c) =>
      c.contact.id === cid
        ? { ...c, contact: { ...c.contact, portal_thread_private: isPrivate } }
        : c
    ));
  }, []);

  // Called by NewClientMessageModal after picking a contact (+participants)
  const handleNewConversationStart = (contact, _participantIds) => {
    setShowNew(false);
    // Ensure the contact appears in the sidebar even if they have no messages yet
    setConversations((prev) => {
      const exists = prev.find((c) => c.contact.id === contact.id);
      if (exists) return prev;
      return [{ contact, lastMessage: null, unreadCount: 0 }, ...prev];
    });
    setActiveId(contact.id);
    setShowThread(true);
  };

  return (
    <div className="flex h-full">
      {showNew && (
        <NewClientMessageModal
          firmId={firmId}
          userId={userId}
          onStart={handleNewConversationStart}
          onClose={() => setShowNew(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`flex-shrink-0 border-r border-slate-100 dark:border-slate-800 flex flex-col
        ${showThread ? 'hidden md:flex' : 'flex'} w-full md:w-72 lg:w-80`}>
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Client Conversations</span>
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition"
              title="New message"
            >
              <Plus size={13} /> New
            </button>
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              className="w-full pl-7 pr-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => setFilterUnread((v) => !v)}
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md transition ${
              filterUnread
                ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            <Inbox size={11} /> {filterUnread ? 'Showing unread only' : 'Show unread only'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationList
            conversations={conversations.filter((c) => {
              if (filterUnread && !(c.unreadCount > 0)) return false;
              const q = query.trim().toLowerCase();
              if (!q) return true;
              const name = contactName(c.contact).toLowerCase();
              const email = (c.contact.email || '').toLowerCase();
              return name.includes(q) || email.includes(q);
            })}
            activeId={activeId}
            onSelect={(id) => { setActiveId(id); setShowThread(true); }}
            loading={loading}
            onNew={() => setShowNew(true)}
          />
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex flex-col min-w-0 ${showThread ? 'flex' : 'hidden md:flex'}`}>
        {activeId ? (
          <>
            <button onClick={() => setShowThread(false)} className="md:hidden flex items-center gap-1.5 text-sm text-indigo-600 px-4 pt-3 pb-1">
              <ChevronLeft size={16} /> All conversations
            </button>
            <PortalThread
              key={activeId}
              contactId={activeId}
              conversations={conversations}
              firmId={firmId}
              userId={userId}
              userProfile={userProfile}
              onMessagesRead={handleMessagesRead}
              onPrivacyChanged={handlePrivacyChanged}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageSquare size={40} className="mb-3 opacity-20" />
            <p className="text-sm">Select a conversation or start a new one</p>
            <button onClick={() => setShowNew(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition">
              <Plus size={15} /> New Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
// ─── Dashboard KPI card ─────────────────────────────────────────────────
// Click-able stat tile rendered in the header strip. Shows a primary count
// plus an unread badge when there's something to attend to.
function OverviewCard({ icon: Icon, label, count, unread, accent, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition border ${
        active
          ? 'border-indigo-500 bg-white dark:bg-slate-900 shadow-sm'
          : 'border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-white dark:hover:bg-slate-900'
      }`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon size={16} className="text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold text-slate-900 dark:text-white tabular-nums">{count ?? 0}</span>
          {unread > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold">
              {unread > 99 ? '99+' : unread} new
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function MessagesPage() {
  const { firmId }     = useFirm();
  const { user, userProfile } = useAuth();
  const [{ searchParams }] = useRoute();
  const [tab, setTab]  = useState('team'); // 'team' | 'clients'
  const [overview, setOverview] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [pendingHighlight, setPendingHighlight] = useState(null); // { scope, container_id, message_id }
  const bookmarks = useBookmarks();

  // Deep-link: #/messages?channel=<id> or #/messages?contact=<id> selects a
  // channel/thread on arrival. Used by MatterDetailPage's "Team channels"
  // panel and any external link into a conversation.
  useEffect(() => {
    if (searchParams?.channel) {
      setTab('team');
      setPendingHighlight({ scope: 'firm', container_id: searchParams.channel });
    } else if (searchParams?.contact) {
      setTab('clients');
      setPendingHighlight({ scope: 'portal', container_id: searchParams.contact });
    }
  }, [searchParams?.channel, searchParams?.contact]);

  // Cmd/Ctrl-K opens the search overlay anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSearchSelect = (hit) => {
    setSearchOpen(false);
    if (hit.scope === 'firm') setTab('team');
    else                       setTab('clients');
    setPendingHighlight(hit);
  };

  // Pull the consolidated overview stats. Refresh on tab switch (cheap, one
  // RPC call) and on a 30-second timer so the unread badges don't go stale
  // while the user is reading.
  const refreshOverview = useCallback(() => {
    getMessagingOverview().then(({ data }) => setOverview(data || {}));
  }, []);
  useEffect(() => {
    if (!firmId) return undefined;
    refreshOverview();
    const t = setInterval(refreshOverview, 30_000);
    return () => clearInterval(t);
  }, [firmId, tab, refreshOverview]);

  return (
    // Height calc has to subtract the chrome around this page:
    //   * AppShell top header (~72px, sticky)
    //   * AppShell <main> top padding (16px on mobile from p-4, 24px on
    //     sm+ from sm:p-6) — pure layout space, not chrome but counts
    //     against available height
    //   * AppShell <main> bottom padding (mobile: 80px fixed bottom-tab
    //     clearance + home-indicator safe-area; lg+: 24px from lg:pb-6)
    //
    // `100dvh` (dynamic viewport height) tracks Safari's visible area
    // as the address bar shows/hides. The old code used `100vh`, which
    // is the LARGE viewport on iOS — the page came out taller than the
    // visible screen and the composer at the bottom of the flex column
    // slipped behind the bottom tab bar.
    //
    // Three breakpoints: default (phone), sm (tablet, padding bumps to
    // p-6), lg (desktop sidebar takes over, mobile tab bar hidden).
    <div className="flex flex-col
                    h-[calc(100dvh-72px-16px-80px-env(safe-area-inset-bottom,0px))]
                    sm:h-[calc(100dvh-72px-24px-80px-env(safe-area-inset-bottom,0px))]
                    lg:h-[calc(100dvh-72px-48px)]">
      {/* Page header */}
      <div className="flex-shrink-0 space-y-4">
        <FirstVisitTips
          pageKey="messages"
          title="Two messaging surfaces, one inbox"
          tips={[
            'Team chat — Slack-style channels for internal firm communication. Each matter can have its own channel.',
            'Client messages — secure conversations with each portal client. Receipts, document requests and case updates flow through here.',
            'Press ⌘K anywhere on this page to search across both surfaces.',
          ]}
        />
        <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Messages</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {overview?.activity_24h != null
                ? <>Team chat and client portal messaging · <strong>{overview.activity_24h}</strong> message{overview.activity_24h === 1 ? '' : 's'} in the last 24h</>
                : 'Team chat and client portal messaging'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBookmarksOpen(true)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-amber-400 hover:text-amber-700 dark:hover:text-amber-300 text-sm text-slate-500 dark:text-slate-400 transition shadow-sm"
              title="Your saved messages"
            >
              <Bookmark size={14} />
              <span className="hidden sm:inline">Saved</span>
              {bookmarks.rows.length > 0 && (
                <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold">
                  {bookmarks.rows.length > 99 ? '99+' : bookmarks.rows.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm text-slate-500 dark:text-slate-400 transition shadow-sm"
              title="Search messages (⌘K)"
            >
              <Search size={14} />
              <span className="hidden sm:inline">Search messages</span>
              <kbd className="hidden sm:inline-block ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800">⌘K</kbd>
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <OverviewCard
            icon={Hash}
            label="Team channels"
            count={overview?.team_channels}
            unread={overview?.team_unread}
            accent="bg-gradient-to-br from-indigo-500 to-indigo-700"
            active={tab === 'team'}
            onClick={() => setTab('team')}
          />
          <OverviewCard
            icon={User}
            label="Direct messages"
            count={overview?.direct_msgs}
            unread={overview?.direct_unread}
            accent="bg-gradient-to-br from-sky-500 to-blue-700"
            active={tab === 'team'}
            onClick={() => setTab('team')}
          />
          <OverviewCard
            icon={MessageSquare}
            label="Client threads"
            count={overview?.client_threads}
            unread={overview?.client_unread}
            accent="bg-gradient-to-br from-emerald-500 to-teal-700"
            active={tab === 'clients'}
            onClick={() => setTab('clients')}
          />
          <OverviewCard
            icon={Bell}
            label="@ Mentions"
            count={overview?.mentions_unread}
            unread={overview?.mentions_unread}
            accent="bg-gradient-to-br from-amber-500 to-orange-700"
            active={false}
            onClick={() => setTab('team')}
          />
        </div>

        {/* Sub-tab pill bar (kept for the secondary navigation between team / clients) */}
        <div className="mt-4 inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-800">
          {[
            { id: 'team',    label: 'Team',    icon: Users },
            { id: 'clients', label: 'Clients', icon: MessageSquare },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === id
                  ? 'bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Panel */}
      <div className="flex-1 min-h-0 mt-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
        {tab === 'team'    && <TeamChat        firmId={firmId} userId={user?.id} userProfile={userProfile}
                                  pendingHighlight={pendingHighlight} onHighlightApplied={() => setPendingHighlight(null)}
                                  bookmarks={bookmarks} />}
        {tab === 'clients' && <ClientMessages  firmId={firmId} userId={user?.id} userProfile={userProfile}
                                  pendingHighlight={pendingHighlight} onHighlightApplied={() => setPendingHighlight(null)} />}
      </div>

      <MessageSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSearchSelect}
      />

      <BookmarksDrawer
        open={bookmarksOpen}
        bookmarks={bookmarks.rows}
        onClose={() => setBookmarksOpen(false)}
      />
    </div>
  );
}
